import { useEffect, useState } from "react";
import { Paper, Grid, Typography, Box, Zoom, useMediaQuery } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { useSelector } from "react-redux";
import { trim, formatCurrency, getEthPrice } from "../../helpers";
import { Swiper, SwiperSlide } from 'swiper/react';
import { useWeb3Context } from "../../hooks/web3Context";
import { useTheme } from "@material-ui/core/styles";
import {
  treasuryDataQuery,
  rebasesDataQuery,
  bulletpoints,
  tooltipItems,
  tooltipInfoMessages,
  itemType,
} from "./treasuryData.js";
import 'swiper/swiper.scss';
import "swiper/components/navigation/navigation.scss";
import "./treasury-dashboard.scss";
import apollo from "../../lib/apolloClient";
import InfoTooltip from "src/components/InfoTooltip/InfoTooltip.jsx";
import Chart from "../../components/Chart/Chart.jsx";

function TreasuryDashboard() {
  const [swiper, setSwiper] = useState(undefined)
  const [swiperIndex, setSwiperIndex] = useState(0)
  const [data, setData] = useState(null);
  const [ethPrice, setEthPrice] = useState(null);
  const [apy, setApy] = useState(null);
  const [runway, setRunway] = useState(null);
  const [staked, setStaked] = useState(null);
  const theme = useTheme();
  const smallerScreen = useMediaQuery("(max-width: 650px)");
  const verySmallScreen = useMediaQuery("(max-width: 379px)");
  const { provider, chainID } = useWeb3Context()

  const marketPrice = useSelector(state => {
    return state.app.marketPrice;
  });
  const circSupply = useSelector(state => {
    return state.app.circSupply;
  });
  const totalSupply = useSelector(state => {
    return state.app.totalSupply;
  });
  const marketCap = useSelector(state => {
    return state.app.marketCap;
  });

  const currentIndex = useSelector(state => {
    return state.app.currentIndex;
  });

  const backingPerGws = useSelector(state => {
    return state.app.treasuryMarketValue / state.app.circSupply;
  });

  useEffect(() => {
    apollo(treasuryDataQuery).then(r => {
      let metrics = r.data.protocolMetrics.map(entry =>
        Object.entries(entry).reduce((obj, [key, value]) => ((obj[key] = parseFloat(value)), obj), {}),
      );
      metrics = metrics.filter(pm => pm.treasuryMarketValue > 0);
      setData(metrics);

      let staked = r.data.protocolMetrics.map(entry => ({
        staked: (parseFloat(entry.sGwsCirculatingSupply) / parseFloat(entry.gwsCirculatingSupply)) * 100,
        timestamp: entry.timestamp,
      }));
      staked = staked.filter(pm => pm.staked < 100);
      setStaked(staked);

      let runway = metrics.filter(pm => pm.runway10k > 5);
      setRunway(runway);
    });

    apollo(rebasesDataQuery).then(r => {
      let apy = r.data.rebases.map(entry => ({
        apy: Math.pow(parseFloat(entry.percentage) + 1, 365 * 3) * 100,
        timestamp: entry.timestamp,
      }));

      apy = apy.filter(pm => pm.apy < 300000);

      setApy(apy);
    });

    getEthPrice(chainID, provider).then((ePrice) => {
      setEthPrice(ePrice);
    })
  }, []);

  return (
    <div id="treasury-dashboard-view" className={`${smallerScreen && "smaller"} ${verySmallScreen && "very-small"}`}>
      <div className="dashboard-container">
        <div className="content-title">
          <Typography variant="h1">Statistics</Typography>
        </div>
        <Box className={`hero-metrics`}>
          <div className="stats-wrapper icon-card-wrapper">
            <div className="swiper-btn prev-btn" style={(swiperIndex <= 0 || window.innerWidth <= 768) ? { display: 'none' } : {}} onClick={() => swiper.slidePrev()}>
              <i className="fa-light fa-chevron-left"></i>
            </div>
            <div className="swiper-btn next-btn" style={(swiperIndex >= 2 || window.innerWidth <= 768) ? { display: 'none' } : {}} onClick={() => swiper.slideNext()}>
              <i className="fa-light fa-chevron-right"></i>
            </div>
            <Swiper
              spaceBetween={8}
              slidesPerView={window.innerWidth <= 768 ? 1 : 4}
              onSlideChange={(swiper) => setSwiperIndex(swiper.activeIndex)}
              onSwiper={(swiper) => setSwiper(swiper)}
            >
              <SwiperSlide>
                <div className="icon-card-wrap metric market">
                  <Paper className="metric-card icon-card">
                    <div className="icon-icon icon-blue">
                      <i className="fa-light fa-landmark"></i>
                    </div>
                    <div className="icon-card-content">
                      <Typography variant="h6" color="textSecondary">
                        Market Cap
                      </Typography>
                      <Typography variant="h4">
                        {marketCap && formatCurrency(marketCap, 0)}
                        {!marketCap && <Skeleton type="text" />}
                      </Typography>
                    </div>
                  </Paper>
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="icon-card-wrap metric price">
                  <Paper className="metric-card icon-card">
                    <div className="icon-icon icon-orange">
                      <i className="fa-light fa-tag"></i>
                    </div>
                    <div className="icon-card-content">
                      <Typography variant="h6" color="textSecondary">
                        GWS Price
                      </Typography>
                      <Typography variant="h4">
                        {/* appleseed-fix */}
                        {marketPrice ? formatCurrency(marketPrice, 2) : <Skeleton type="text" />}
                      </Typography>
                    </div>
                  </Paper>
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="icon-card-wrap metric wsoprice">
                  <Paper className="metric-card icon-card">
                    <div className="icon-icon icon-red">
                      <i className="fa-light fa-chart-column"></i>
                    </div>
                    <div className="icon-card-content">
                      <Typography variant="h6" color="textSecondary">
                        ETH Price
                      </Typography>
                      <Typography variant="h4">
                        {ethPrice ? formatCurrency(ethPrice, 2) : <Skeleton type="text" />}
                      </Typography>
                    </div>
                  </Paper>
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="icon-card-wrap metric circ">
                  <Paper className="metric-card icon-card">
                    <div className="icon-icon icon-blue">
                      <i className="fa-light fa-chart-column"></i>
                    </div>
                    <div className="icon-card-content">
                      <Typography variant="h6" color="textSecondary">
                        Circulating Supply
                      </Typography>
                      <Typography variant="h4">
                        {circSupply && totalSupply ? (
                          parseInt(circSupply) + " / " + parseInt(totalSupply)
                        ) : (
                          <Skeleton type="text" />
                        )}
                      </Typography>
                    </div>
                  </Paper>
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="icon-card-wrap metric bpo">
                  <Paper className="metric-card icon-card">
                    <div className="icon-icon icon-orange">
                      <i className="fa-light fa-chart-column"></i>
                    </div>
                    <div className="icon-card-content">
                      <Typography variant="h6" color="textSecondary">
                        Backing per GWS
                      </Typography>
                      <Typography variant="h4">
                        {backingPerGws ? formatCurrency(backingPerGws, 2) : <Skeleton type="text" />}
                      </Typography>
                    </div>
                  </Paper>
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="icon-card-wrap metric index">
                  <Paper className="metric-card icon-card">
                    <div className="icon-icon icon-red">
                      <i className="fa-light fa-chart-column"></i>
                    </div>
                    <div className="icon-card-content">
                      <Typography variant="h6" color="textSecondary">
                        Current Index
                        <InfoTooltip
                          message={
                            "The current index tracks the amount of sGWS accumulated since the beginning of staking. Basically, how much sGWS one would have if they staked and held a single GWS from day 1."
                          }
                        />
                      </Typography>
                      <Typography variant="h4">
                        {currentIndex ? trim(currentIndex, 2) + " sGWS" : <Skeleton type="text" />}
                      </Typography>
                    </div>
                  </Paper>
                </div>
              </SwiperSlide>
            </Swiper>
          </div>
        </Box>

        <div className="content-title">
          <Typography variant="h2">Overview</Typography>
        </div>
        <Zoom in={true}>
          <Grid container spacing={2} className="data-grid">
            <Grid item lg={4} md={4} sm={6} xs={12}>
              <Paper className="stats-card stats-chart-card">
                <Chart
                  type="line"
                  data={data}
                  dataKey={["totalValueLocked"]}
                  color={'#1E85FF'}
                  headerText="Total Value Deposited"
                  headerSubText={`${data && formatCurrency(data[0].totalValueLocked)}`}
                  bulletpointColors={bulletpoints().tvl}
                  itemNames={tooltipItems.tvl}
                  itemType={itemType.dollar}
                  infoTooltipMessage={tooltipInfoMessages.tvl}
                  expandedGraphStrokeColor={theme.palette.graphStrokeColor}
                />
              </Paper>
            </Grid>

            <Grid item lg={4} md={4} sm={6} xs={12}>
              <Paper className="stats-card stats-chart-card">
                <Chart
                  type="stackLine"
                  data={data}
                  dataKey={["treasuryDaiMarketValue"]}
                  stroke={["#1E85FF", "#ff5613"]}
                  headerText="Market Value of Treasury Assets"
                  headerSubText={`${data && formatCurrency(data[0].treasuryMarketValue)}`}
                  bulletpointColors={bulletpoints(["#1E85FF", "#ff5613"]).coin}
                  itemNames={tooltipItems.coin}
                  itemType={itemType.dollar}
                  infoTooltipMessage={tooltipInfoMessages.mvt}
                  expandedGraphStrokeColor={theme.palette.graphStrokeColor}
                />
              </Paper>
            </Grid>

            <Grid item lg={4} md={4} sm={6} xs={12}>
              <Paper className="stats-card stats-chart-card">
                <Chart
                  type="stackLine"
                  format="currency"
                  data={data}
                  dataKey={["treasuryDaiRiskFreeValue"]}
                  stroke={["#1E85FF", "#ff5613"]}
                  headerText="Risk Free Value of Treasury Assets"
                  headerSubText={`${data && formatCurrency(data[0].treasuryRiskFreeValue)}`}
                  bulletpointColors={bulletpoints(["#1E85FF"]).coin}
                  itemNames={tooltipItems.coin}
                  itemType={itemType.dollar}
                  infoTooltipMessage={tooltipInfoMessages.rfv}
                  expandedGraphStrokeColor={theme.palette.graphStrokeColor}
                />
              </Paper>
            </Grid>

            <Grid item lg={4} md={4} sm={6} xs={12}>
              <Paper className="stats-card">
                <Chart
                  type="line"
                  data={data}
                  dataKey={["treasuryGwsDaiPOL"]}
                  color={'#ff5613'}
                  headerText="Protocol Owned Liquidity GWS-DAI"
                  headerSubText={`${data && trim(data[0].treasuryGwsDaiPOL, 2)}% `}
                  bulletpointColors={bulletpoints(null, null, ['#ff5613']).pol}
                  itemNames={tooltipItems.pol}
                  itemType={itemType.percentage}
                  infoTooltipMessage={tooltipInfoMessages.pol}
                  expandedGraphStrokeColor={theme.palette.graphStrokeColor}
                />
              </Paper>
            </Grid>

            <Grid item lg={4} md={4} sm={6} xs={12}>
              <Paper className="stats-card">
                <Chart
                  type="line"
                  data={staked}
                  dataKey={["staked"]}
                  color={'#fd2a72'}
                  headerText="GWS Staked"
                  isStaked={true}
                  headerSubText={`${staked && trim(staked[0].staked, 2)}% `}
                  bulletpointColors={bulletpoints(null, null, ['#fd2a72', '#fd2a72']).pol}
                  itemNames={tooltipItems.pol}
                  itemType={itemType.percentage}
                  infoTooltipMessage={tooltipInfoMessages.staked}
                  expandedGraphStrokeColor={theme.palette.graphStrokeColor}
                />
              </Paper>
            </Grid>

            {(apy && apy.length > 0) &&
              <Grid item lg={4} md={4} sm={6} xs={12}>
                <Paper className="stats-card">
                  <Chart
                    type="line"
                    scale="log"
                    data={apy}
                    dataKey={["apy"]}
                    color={'#1E85FF'}
                    stroke={[theme.palette.text.primary]}
                    headerText="APY over time"
                    dataFormat="percent"
                    headerSubText={`${apy && trim(apy[0].apy, 2)}%`}
                    bulletpointColors={bulletpoints(null, null, null, '#1E85FF').apy}
                    itemNames={tooltipItems.apy}
                    itemType={itemType.percentage}
                    infoTooltipMessage={tooltipInfoMessages.apy}
                    expandedGraphStrokeColor={theme.palette.graphStrokeColor}
                  />
                </Paper>
              </Grid>
            }

            {runway && runway.length > 0 &&
              <Grid item lg={4} md={4} sm={6} xs={12}>
                <Paper className="stats-card">
                  <Chart
                    type="line"
                    data={runway}
                    dataKey={["runwayCurrent"]}
                    color={'#62aaff'}
                    stroke={[theme.palette.text.primary]}
                    headerText="Runway Available"
                    headerSubText={`${data && trim(data[0].runwayCurrent, 1)} Days`}
                    dataFormat="days"
                    bulletpointColors={bulletpoints(null, null, null, null, ['#62aaff', '#ff5613', '#fd2a72']).runway}
                    itemNames={tooltipItems.runway}
                    itemType={""}
                    infoTooltipMessage={tooltipInfoMessages.runway}
                    expandedGraphStrokeColor={theme.palette.graphStrokeColor}
                  />
                </Paper>
              </Grid>
            }
          </Grid>
        </Zoom>
      </div>
    </div>
  );
}

export default TreasuryDashboard;
